import { formatDuration } from '@/helpers/formatTime';
import { alpha, brand } from '@/styles/shared';
import { Box, Button, Typography } from '@mui/material';
import { recordingControlsStyles } from './styles';

// Import assets
import micGreen from '@/assets/micGreen.png';
import micRed from '@/assets/micRed.png';
import pauseBtn from '@/assets/pauseBtn.png';

type RecordingState =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'processing';

interface RecordingControlsProps {
  recordingState: RecordingState;
  recordingTime: number;
  onStopRecording: () => void;
  onPauseResume: () => void;
  volumeLevel: number;
}

const RecordingControls: React.FC<RecordingControlsProps> = ({
  recordingState,
  recordingTime,
  onStopRecording,
  onPauseResume,
  volumeLevel,
}) => {
  const getMicrophoneImage = () => {
    if (recordingState === 'recording') return micGreen;
    if (recordingState === 'paused') return micRed;
    return micRed; // default
  };

  const getMicrophoneBorderColor = () => {
    if (recordingState === 'recording') return alpha.green20;
    if (recordingState === 'paused') return alpha.red10;
    return alpha.green10;
  };

  return (
    <Box sx={recordingControlsStyles.container}>
      {/* End Recording Button */}
      <Box sx={recordingControlsStyles.endButtonContainer}>
        <Button
          variant="contained"
          onClick={onStopRecording}
          sx={recordingControlsStyles.endButton}
        >
          End Recording
        </Button>
      </Box>

      {/* Pause Button and Microphone - Side by side */}
      <Box sx={recordingControlsStyles.controlsContainer}>
        {/* Pause Button */}
        <Box
          sx={{
            ...recordingControlsStyles.pauseButton,
            border: `0.26px solid ${recordingState === 'paused' ? brand.redAlert : '#000000'}`,
            boxShadow: `0px 0px 5px 0.5px ${recordingState === 'paused' ? alpha.red30 : 'rgba(226, 226, 226, 1)'}`,
          }}
          onClick={onPauseResume}
        >
          <Box
            component="img"
            src={pauseBtn}
            alt="Pause"
            sx={{
              ...recordingControlsStyles.pauseIcon,
              filter:
                recordingState === 'paused'
                  ? 'brightness(0) saturate(100%) invert(12%) sepia(100%) saturate(7500%) hue-rotate(0deg) brightness(95%) contrast(107%)'
                  : 'none',
            }}
          />
        </Box>

        {/* Microphone + Volume level arcs */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Microphone button with white background */}
          <Box
            sx={{
              ...recordingControlsStyles.microphoneButton,
              boxShadow: `0px 0px 10px 1px ${getMicrophoneBorderColor()}`,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              component="img"
              src={getMicrophoneImage()}
              alt="Microphone"
              sx={{
                ...recordingControlsStyles.microphoneIcon,
                filter:
                  recordingState === 'recording'
                    ? 'brightness(0) saturate(100%) invert(64%) sepia(84%) saturate(2500%) hue-rotate(63deg) brightness(95%) contrast(95%)' // Bright green
                    : recordingState === 'paused'
                      ? 'brightness(0) saturate(100%) invert(12%) sepia(100%) saturate(7500%) hue-rotate(0deg) brightness(95%) contrast(107%)' // Red
                      : 'brightness(0) saturate(100%) invert(48%) sepia(14%) saturate(1500%) hue-rotate(80deg) brightness(95%) contrast(90%)', // Dark green tint
              }}
            />
            <Typography variant="body2" sx={recordingControlsStyles.timerText}>
              {formatDuration(recordingTime)}
            </Typography>
          </Box>

          {/* Volume level arc segments - rendered on top */}
          {[0, 1, 2, 3, 4, 5, 6, 7].map(level => {
            const isActive = volumeLevel > level / 8;
            // Arrange in semicircle from -50° to +50° (100° arc above mic)
            const angle = -50 + level * 14.3; // Distribute 8 bars across 100° arc
            const radius = 24; // Smaller radius to keep bars inside
            const x = Math.sin((angle * Math.PI) / 180) * radius;
            const y = -Math.cos((angle * Math.PI) / 180) * radius;

            return (
              <Box
                key={level}
                sx={{
                  position: 'absolute',
                  left: `calc(50% + ${x}px)`,
                  top: `calc(50% + ${y}px)`,
                  width: '2px',
                  height: '8px',
                  backgroundColor:
                    recordingState === 'recording'
                      ? isActive
                        ? '#4CAF50'
                        : 'rgba(76, 175, 80, 0.2)'
                      : recordingState === 'paused'
                        ? isActive
                          ? '#D32F2F'
                          : 'rgba(211, 47, 47, 0.2)'
                        : 'rgba(76, 175, 80, 0.15)',
                  borderRadius: '1.5px',
                  transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                  transformOrigin: 'center',
                  transition: 'background-color 0.08s ease',
                  zIndex: 2,
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export { RecordingControls };
